import { useRef, useCallback, useMemo } from "react";
import { createSidebarTemplateSelectionSession } from "./sidebarTemplateSelectionSession";

/**
 * Stable ref-backed template selection session for sidebar dictation.
 */
export function useSidebarTemplateSelection(options) {
  const sessionRef = useRef(null);
  if (!sessionRef.current) {
    sessionRef.current = createSidebarTemplateSelectionSession(options);
  }

  const session = sessionRef.current;

  const resetForMicSession = useCallback(
    (options) => {
      session.resetForMicSession(options);
    },
    [session],
  );

  const onTranscriptCleared = useCallback(() => {
    session.onTranscriptCleared();
  }, [session]);

  const onTemplateSelectionCleared = useCallback(() => {
    session.onTemplateSelectionCleared();
  }, [session]);

  const updateSidebarText = useCallback(
    (text) => session.updateSidebarText(text),
    [session],
  );

  const ingestRawCumulative = useCallback(
    (rawCumulative) => session.ingestRawCumulative(rawCumulative),
    [session],
  );

  const appendSonioxFinalDelta = useCallback(
    (delta) => session.appendSonioxFinalDelta(delta),
    [session],
  );

  const hasSonioxMatchAnchor = useCallback(
    () => session.hasSonioxMatchAnchor(),
    [session],
  );

  const getVoiceTemplateGate = useCallback(
    (gateOptions) => session.getVoiceTemplateGate(gateOptions),
    [session],
  );

  const getRawCumulative = useCallback(() => session.rawCumulative, [session]);

  const getSessionText = useCallback(() => session.sessionText, [session]);

  const getOriginalFirstWords = useCallback(
    () => session.getOriginalFirstWords(),
    [session],
  );

  const isAutoSelectEnabled = useCallback(
    () => session.autoSelectEnabled,
    [session],
  );

  const finalizeAfterTemplateAttempt = useCallback(
    () => session.finalizeAfterTemplateAttempt(),
    [session],
  );

  const lockAfterTemplateSelected = useCallback(
    () => session.lockAfterTemplateSelected(),
    [session],
  );

  return useMemo(
    () => ({
      resetForMicSession,
      onTranscriptCleared,
      onTemplateSelectionCleared,
      updateSidebarText,
      ingestRawCumulative,
      appendSonioxFinalDelta,
      hasSonioxMatchAnchor,
      getVoiceTemplateGate,
      getRawCumulative,
      getSessionText,
      getOriginalFirstWords,
      isAutoSelectEnabled,
      finalizeAfterTemplateAttempt,
      lockAfterTemplateSelected,
    }),
    [
      resetForMicSession,
      onTranscriptCleared,
      onTemplateSelectionCleared,
      updateSidebarText,
      ingestRawCumulative,
      appendSonioxFinalDelta,
      hasSonioxMatchAnchor,
      getVoiceTemplateGate,
      getRawCumulative,
      getSessionText,
      getOriginalFirstWords,
      isAutoSelectEnabled,
      finalizeAfterTemplateAttempt,
      lockAfterTemplateSelected,
    ],
  );
}
